import * as Y from 'yjs';
import { WebrtcProvider } from 'y-webrtc';
import { IndexeddbPersistence } from 'y-indexeddb';
import type { Awareness } from 'y-protocols/awareness';
import { getIdentity } from './identity';

export interface CollabContext {
  ydoc: Y.Doc;
  ytext: Y.Text;
  provider: WebrtcProvider;
  persistence: IndexeddbPersistence;
  awareness: Awareness;
  undoManager: Y.UndoManager;
  roomId: string;
}

export async function createCollab(roomId: string, seedContent?: string): Promise<CollabContext> {
  const ydoc = new Y.Doc();
  const persistence = new IndexeddbPersistence(`md-room-${roomId}`, ydoc);
  await persistence.whenSynced;

  const ytext = ydoc.getText('content');

  // Seed content into a fresh room (only if Y.Text is empty after IDB hydrate)
  if (ytext.length === 0 && seedContent) {
    ydoc.transact(() => ytext.insert(0, seedContent));
  }

  const signaling = import.meta.env.VITE_SIGNALING?.split(',') ?? [
    location.protocol === 'https:'
      ? `wss://${location.host}/signaling`
      : `ws://${location.hostname}:4444`,
  ];
  const provider = new WebrtcProvider(roomId, ydoc, { signaling });
  const awareness = provider.awareness;

  const me = getIdentity();
  awareness.setLocalStateField('user', { name: me.name, color: me.color });

  const undoManager = new Y.UndoManager(ytext);

  return { ydoc, ytext, provider, persistence, awareness, undoManager, roomId };
}

export function destroyCollab(ctx: CollabContext): void {
  try { ctx.provider.destroy(); } catch { /* */ }
  try { ctx.persistence.destroy(); } catch { /* */ }
  try { ctx.ydoc.destroy(); } catch { /* */ }
}

export interface RemotePeer {
  clientId: number;
  name: string;
  color: string;
}

export function getRemotePeers(ctx: CollabContext): RemotePeer[] {
  const out: RemotePeer[] = [];
  ctx.awareness.getStates().forEach((s: any, clientId: number) => {
    if (clientId === ctx.awareness.clientID) return;
    if (!s?.user) return;
    out.push({ clientId, name: s.user.name, color: s.user.color });
  });
  return out;
}
